import { Check, X } from "lucide-react";
import { cn } from "@/lib/cn";
import type { PasswordChecks } from "@/lib/password";

type Item = { label: string; ok: boolean };

/**
 * PasswordChecklist muestra en vivo los requisitos de la contraseña: cada ítem se
 * marca en verde al cumplirse y queda en gris mientras no. Es el componente
 * compartido por el cambio de clave (`/mi-clave`) y el restablecimiento
 * (`/restablecer`) (SPEC #198, bloques 1 y 4).
 *
 * `coincide` añade el ítem «las contraseñas coinciden» cuando el formulario tiene un
 * campo de confirmación (undefined lo omite). `resaltar` pinta en rojo los requisitos
 * aún sin cumplir tras un rechazo del servidor, para que el porqué quede a la vista.
 */
export function PasswordChecklist({
  checks,
  coincide,
  resaltar = false,
}: {
  checks: PasswordChecks;
  coincide?: boolean;
  resaltar?: boolean;
}) {
  const items: Item[] = [
    { label: "Al menos 8 caracteres", ok: checks.longitud },
    { label: "Al menos una letra", ok: checks.letra },
    { label: "Al menos un número", ok: checks.numero },
  ];
  if (coincide !== undefined) {
    items.push({ label: "Las contraseñas coinciden", ok: coincide });
  }

  return (
    <ul className="flex flex-col gap-1.5" aria-label="Requisitos de la contraseña">
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-2 text-sm">
          {item.ok ? (
            <Check className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
          ) : (
            <X
              className={cn("h-4 w-4 shrink-0", resaltar ? "text-red-500" : "text-slate-400")}
              aria-hidden="true"
            />
          )}
          <span
            className={cn(
              item.ok ? "text-emerald-700" : resaltar ? "text-red-600" : "text-slate-500",
            )}
          >
            {item.label}
          </span>
        </li>
      ))}
    </ul>
  );
}
